import { get, toLower, toUpper } from 'lodash';
import { pathToRegexp } from 'path-to-regexp';
import moment from 'moment';
import { b2cPagesService, constants } from '@clubmed/utils';
import { getSiteSection, noCase } from 'Tracking/utils';
import {
  getDepartureCityCode,
  getDepartureCityName,
  getTrackingTransport,
} from 'Utils/tracking/resort';
import {
  getDepartureDateRange,
  getExclusiveCollectionFromComfort,
} from 'Views/booking/BookingEngine.utils';
import { momentFromApiDate } from 'Utils/date/apiDate';
const { PRODUCT_TYPES } = constants;
const { PAGE_TYPES } = b2cPagesService;
type matchedPath = {
  matched: boolean;
  url?: string;
  params?: Record<string, string>;
};

function matchPath(path: string, pathSchema: string): matchedPath {
  const keys = [];
  const regexp = pathToRegexp(pathSchema, keys);
  const match = regexp.exec(path);

  if (match) {
    const [url, ...paramValues] = match;
    return {
      matched: true,
      url,
      params: keys.reduce((memo, key, index) => ({ ...memo, [key.name]: paramValues[index] }), {}),
    };
  }

  return {
    matched: false,
  };
}

type pageProps = PageContext & {
  pagePath: string;
  pageData?: any;
};
export function getDefaultTrackingProps({ pageId, pageType }: pageProps) {
  const siteSection = getSiteSection({
    pageType,
  });
  return {
    siteSection,
    pageName: `${siteSection}_${pageId}`,
  };
}
export function getDestinationTrackingProps({
  pageType,
  pageRegion: { name: destinationRegion } = {
    name: null,
  },
  pageCountry: { name: destinationCountry } = {
    name: null,
  },
}: pageProps) {
  const siteSection = getSiteSection({
    pageType,
  });
  // The default productType for 'destination' && 'destination-mice' pages is 'resort',
  // Otherwise it is specified after the hyphen, e.g. 'destination-tour' or 'destination-cruise'.
  const productType =
    pageType === PAGE_TYPES.DESTINATION || pageType === PAGE_TYPES.DESTINATION_MICE
      ? PAGE_TYPES.RESORT
      : pageType.split('-')[1];
  const productTypes = Object.keys(PRODUCT_TYPES).map(toLower);
  const isIndexPage = !destinationRegion && !destinationCountry;
  let pageName = siteSection;

  if (!isIndexPage) {
    const pageIdentifier = [destinationRegion, destinationCountry]
      .filter((item) => !!item)
      .join('_');
    pageName = `${siteSection}_${pageIdentifier}`;
  }

  return {
    siteSection,
    pageName,
    destinationRegion,
    destinationCountry,
    ...(productTypes.includes(productType)
      ? {
          productType: toUpper(productType),
        }
      : null),
  };
}
export function getDestinationTravelTrackingProps({
  pageId,
  pageMeta,
  pageRegion: { name: destinationRegion } = {
    name: null,
  },
  pageCountry: { name: destinationCountry } = {
    name: null,
  },
}: pageProps) {
  // 'Travel' section != 'DestinationTravel' pageType to preserve historical tracking consistency
  const siteSection = 'Shopping_Travel';
  const { name = null } = pageMeta;
  const pageIdentifier = name ? name : pageId;
  return {
    siteSection,
    pageName: `${siteSection}_${pageIdentifier}`,
    destinationRegion,
    destinationCountry,
  };
}
export function getLandingTrackingProps(
  { pageId, pageType, pageMeta }: pageProps,
  resort?: {
    readonly id: string;
    readonly type: string;
  },
) {
  const isSummerCamp = resort?.type === PRODUCT_TYPES.SUMMER_CAMP;
  const siteSection = getSiteSection({
    pageType,
  });
  const { name = null } = pageMeta;
  const pageIdentifier = name ? name : pageId;

  if (isSummerCamp && resort) {
    return {
      siteSection,
      pageName: `${siteSection}_SummerCamp_${resort.id}`,
      categoryGA: pageIdentifier,
    };
  }

  return {
    siteSection,
    pageName: `${siteSection}_${pageIdentifier}`,
  };
}
export function getOfferTrackingProps({ pagePath }: pageProps) {
  // 'Offers' section != 'Offer' pageType to preserve historical tracking consistency
  const siteSection = 'Shopping_Offers';
  const { params: { offerSlug } = {} } = matchPath(pagePath, '/o/:offerSlug?');
  const pageName = offerSlug ? `${siteSection}_${noCase(offerSlug)}` : siteSection;
  return {
    siteSection,
    pageName,
    channel: 'Offers',
  };
}
export function getPrebookingTrackingProps({ pageId, pageType }: pageProps) {
  const siteSection = getSiteSection({
    pageType,
  });
  return {
    siteSection,
    pageName: `${siteSection}_${pageId}`,
  };
}
export function getHomepageTrackingProps({ pageId }: pageProps) {
  const siteSection = 'Shopping_Home';
  const categoryGA = pageId.toLowerCase();
  return {
    categoryGA,
    siteSection,
    pageName: `${siteSection}`,
  };
}
export function resolveDepartureDateTracking(departureDate: string) {
  const daysBeforeDapture = momentFromApiDate(departureDate).diff(moment(), 'days');
  return {
    resort_departure_date: departureDate,
    departure_quarter: momentFromApiDate(departureDate).quarter(),
    departure_semester: momentFromApiDate(departureDate).quarter() > 2 ? 2 : 1,
    departure_date_range: getDepartureDateRange(daysBeforeDapture),
    resort_year_season: momentFromApiDate(departureDate).format('YYYY'),
  };
}
export function getTourTrackingProps({ pageType, pageData = {} }: pageProps) {
  const siteSection = getSiteSection({
    pageType,
  });
  const { resort = {}, price = {}, bookingCriteria = {} } = pageData;
  const resortId = get(resort, 'id');
  // '_Main' is appended to resort pageNames to preserve historical tracking consistency
  const pageName = `${siteSection.toLowerCase()}_${resortId}`;
  const departureCityCode = get(price, 'termsAndConditions.departureCity.value');
  const departureCityName = get(price, 'termsAndConditions.departureCity.name');
  const departureDate = get(price, 'termsAndConditions.departureDate', null);
  return {
    ...resolveDepartureDateTracking(departureDate),
    page_name: pageName,
    site_section: siteSection,
    isProductPage: true,
    resort_code: get(resort, 'id', null),
    resort_country: toUpper(get(resort, 'destination.country', '')),
    resort_name: toUpper(get(resort, 'name', '')),
    resort_season_type: get(resort, 'seasonName', null),
    resort_region: toUpper(get(resort, 'destination.area', '')),
    resort_type: get(resort, 'type', null),
    resort_year_season: '',
    resort_exclusive_collection: getExclusiveCollectionFromComfort(
      resort?.comfort || '',
      resort?.hasECSpace || '',
    ),
    resort_url_image: get(resort, 'image', null),
    resort_participants:
      (bookingCriteria?.initialNumberOfAdults || 0) +
      (bookingCriteria?.initialNumberOfChildren || 0),
    resort_night_number: bookingCriteria?.initialDuration || 1,
    resort_adult: bookingCriteria?.initialNumberOfAdults || 0,
    resort_children: bookingCriteria?.initialNumberOfChildren || 0,
    resort_departure_city_name: toUpper(getDepartureCityName(departureCityCode, departureCityName)),
    resort_default_price: get(price, 'bestPrice.value', null),
  };
}
export function getResortTrackingProps({ pageType, pageData = {} }: pageProps) {
  const siteSection = getSiteSection({
    pageType,
  });
  const { resort = {}, price = {}, bookingCriteria = null } = pageData;
  const resortId = get(resort, 'id');
  // '_Main' is appended to resort pageNames to preserve historical tracking consistency
  const pageName =
    get(resort, 'type', '') === PRODUCT_TYPES.RESORT
      ? `${siteSection}_${resortId}_Main`
      : `${siteSection}_${resortId}`;
  const departureCityCode = get(price, 'termsAndConditions.departureCity.value');
  const departureCityName = get(price, 'termsAndConditions.departureCity.name');
  return {
    isProductPage: true,
    categoryGA: `${pageType}-${resortId}`,
    siteSection,
    pageName,
    resortCode: get(resort, 'id', null),
    resortCountry: toUpper(get(resort, 'destination.country', '')),
    resortDefaultPrice: get(price, 'bestPrice.value', null),
    resortDepartureCityCode: toUpper(getDepartureCityCode(departureCityCode)),
    resortDepartureCityName: toUpper(getDepartureCityName(departureCityCode, departureCityName)),
    resortDepartureDate: get(price, 'termsAndConditions.departureDate', null),
    resortName: toUpper(get(resort, 'name', '')),
    resortOldPrice: get(price, 'initialPrice.value', null),
    resortRegion: toUpper(get(resort, 'destination.area', '')),
    resortSeasonType: get(resort, 'seasonName', null),
    resortTransport: toUpper(getTrackingTransport(departureCityCode)),
    resortTrident: get(resort, 'comfort', null),
    resortUrlImage: get(resort, 'image', null),
    bookingCriteria,
  };
}
